import {
  type CancellableRenderTask,
  getOutputScale,
  getPdfPageLayout,
  isRenderCanceledError,
  type PdfPageBaseLayout,
  Box,
  FontAwesomeIcon,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { STRETCH_MODAL_CONTENT_VIEWPORT_OFFSET_PX } from "@/constants/ui/stretch-modal-content.constants";
import { getRoleMarkerClass } from "@/pages/esign/esign.config.ts";
import { type PDFDocumentProxy } from "pdfjs-dist";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

const pageMenuThumbnailWidth = 105;

export type EsignPageNavigationPlacement = "fixed" | "inside" | "none" | "left";

interface EsignPageNavigationProps {
  placement: EsignPageNavigationPlacement;
  pdf: PDFDocumentProxy;
  pageCount: number;
  activePage: number;
  baseLayouts: Record<number, PdfPageBaseLayout>;
  pageMarkerRoleIndexes?: Record<number, number[]>;
  isPageMenuOpen: boolean;
  onPageMenuOpen: () => void;
  onPageClick: (pageNumber: number) => void;
  onRenderError: (message: string) => void;
}

export function EsignPageNavigation({
  placement,
  pdf,
  pageCount,
  activePage,
  baseLayouts,
  pageMarkerRoleIndexes,
  isPageMenuOpen,
  onPageMenuOpen,
  onPageClick,
  onRenderError,
}: EsignPageNavigationProps) {
  const { t } = useTranslation();
  const pageMenuRef = useRef<HTMLDivElement | null>(null);
  const pageMenuCanvasRefs = useRef<Record<number, HTMLCanvasElement | null>>({});
  const pageMenuItemRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  useEffect(() => {
    if (!isPageMenuOpen) return;

    let isActive = true;
    const renderTasks: CancellableRenderTask[] = [];

    const renderPageMenu = async () => {
      try {
        const outputScale = getOutputScale();
        const transform =
          outputScale === 1
            ? undefined
            : ([outputScale, 0, 0, outputScale, 0, 0] satisfies [
                number,
                number,
                number,
                number,
                number,
                number,
              ]);

        for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
          if (!isActive) return;

          const canvas = pageMenuCanvasRefs.current[pageNumber];
          const baseLayout = baseLayouts[pageNumber];
          if (!canvas || !baseLayout) continue;

          const layout = getPdfPageLayout(baseLayout, pageMenuThumbnailWidth, 1);
          canvas.style.width = `${layout.cssWidth}px`;
          canvas.style.height = `${layout.cssHeight}px`;
          canvas.width = Math.max(1, Math.floor(layout.cssWidth * outputScale));
          canvas.height = Math.max(1, Math.floor(layout.cssHeight * outputScale));

          const page = await pdf.getPage(pageNumber);
          const viewport = page.getViewport({ scale: pageMenuThumbnailWidth / baseLayout.width });
          const renderTask = page.render({ canvas, transform, viewport });
          renderTasks.push(renderTask);
          await renderTask.promise;
        }
      } catch (renderError: unknown) {
        if (!isActive || isRenderCanceledError(renderError)) return;
        onRenderError(
          renderError instanceof Error ? renderError.message : t("common:pdf_render_failed")
        );
      }
    };

    void renderPageMenu();

    return () => {
      isActive = false;
      renderTasks.forEach((task) => {
        task.cancel();
      });
    };
  }, [baseLayouts, isPageMenuOpen, onRenderError, pageCount, pdf, t]);

  useEffect(() => {
    if (!isPageMenuOpen) return;

    const menu = pageMenuRef.current;
    const item = pageMenuItemRefs.current[activePage];
    if (!menu || !item) return;

    const menuRect = menu.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();

    if (itemRect.top < menuRect.top) {
      menu.scrollTop -= menuRect.top - itemRect.top;
    } else if (itemRect.bottom > menuRect.bottom) {
      menu.scrollTop += itemRect.bottom - menuRect.bottom;
    }
  }, [activePage, isPageMenuOpen]);

  const handlePageClick = (pageNumber: number) => {
    if (placement === "inside") onPageMenuOpen();
    onPageClick(pageNumber);
  };

  const pageNavigation = (
    <Stack
      direction={"vertical"}
      items={"start"}
      className={placement === "fixed" ? "" : "sticky top-1/2 z-10 -translate-y-1/2 self-start"}
    >
      {Array.from({ length: pageCount }, (_, index) => {
        const pageNumber = index + 1;
        const isActive = pageNumber === activePage;

        return (
          <Tooltip delay={false} key={`page-nav-${pageNumber}`}>
            <TooltipTrigger asChild={true}>
              <button
                type={"button"}
                onClick={() => {
                  handlePageClick(pageNumber);
                }}
                className={"group flex cursor-pointer p-1.5"}
              >
                <span
                  className={
                    "h-px w-4 rounded-full transition-colors " +
                    (isActive ? "bg-dark/70" : "bg-dark/20 group-hover:bg-dark/40")
                  }
                />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <Stack gap={"xs"}>
                <Text className={"text-light"}>
                  {t("common:page_of", { page: pageNumber, total: pageCount })}
                </Text>
                {placement === "inside" && !isPageMenuOpen ? (
                  <Text className={"text-light/60"}>{t("common:click_to_show_pages")}</Text>
                ) : null}
              </Stack>
            </TooltipContent>
          </Tooltip>
        );
      })}
    </Stack>
  );

  if (placement === "fixed") {
    return (
      <div
        className={
          "fixed inset-y-0 left-0 z-10 flex items-center px-6 transition-colors hover:bg-dark/5"
        }
      >
        {pageNavigation}
      </div>
    );
  }

  if (placement === "left") {
    return (
      <Box
        className={"relative -ml-6 shrink-0 self-stretch px-6 transition-colors hover:bg-dark/5"}
      >
        {pageNavigation}
      </Box>
    );
  }

  if (placement !== "inside") return null;

  return (
    <Stack
      direction={"horizontal"}
      className={twMerge(
        "-ml-6 shrink-0 gap-4 self-stretch py-2 pl-6 transition-colors hover:bg-dark/5",
        !isPageMenuOpen && "absolute inset-y-0 left-0 pr-6"
      )}
    >
      <Box className={"relative shrink-0 self-stretch"}>{pageNavigation}</Box>
      {isPageMenuOpen ? (
        <div
          ref={pageMenuRef}
          className={
            "sticky top-4 z-10 w-36 shrink-0 overflow-y-auto overscroll-contain py-2 pr-6 [scrollbar-gutter:stable]"
          }
          style={{
            maxHeight: `calc(100dvh - ${STRETCH_MODAL_CONTENT_VIEWPORT_OFFSET_PX}px)`,
          }}
        >
          <Stack direction={"vertical"} items={"center"} className={"gap-3"}>
            {Array.from({ length: pageCount }, (_, index) => {
              const pageNumber = index + 1;
              const isActive = pageNumber === activePage;
              const markerRoleIndexes = pageMarkerRoleIndexes?.[pageNumber] ?? [];

              return (
                <Stack key={`page-menu-${pageNumber}`} items={"center"} className={"gap-1"}>
                  <Text className={"title-sub-headline text-secondary"}>
                    {String(pageNumber).padStart(2, "0")}
                  </Text>
                  <button
                    ref={(el) => {
                      pageMenuItemRefs.current[pageNumber] = el;
                    }}
                    type={"button"}
                    aria-label={t("common:page_of", { page: pageNumber, total: pageCount })}
                    onClick={() => {
                      onPageClick(pageNumber);
                    }}
                    className={
                      "relative cursor-pointer rounded-md border bg-light p-0 shadow-sm transition-colors " +
                      (isActive ? "border-dark" : "border-dark/10 hover:border-dark/40")
                    }
                  >
                    {markerRoleIndexes.length > 0 ? (
                      <span className={"absolute left-2 top-2 z-10 flex flex-col gap-0.5"}>
                        {markerRoleIndexes.map((roleIndex) => (
                          <FontAwesomeIcon
                            key={`page-menu-${pageNumber}-role-${roleIndex}`}
                            icon={"fa-solid fa-label"}
                            className={`h-4 w-4 ${getRoleMarkerClass(roleIndex)}`}
                          />
                        ))}
                      </span>
                    ) : null}
                    <canvas
                      ref={(el) => {
                        pageMenuCanvasRefs.current[pageNumber] = el;
                      }}
                      className={"block rounded-md"}
                    />
                  </button>
                </Stack>
              );
            })}
          </Stack>
        </div>
      ) : null}
    </Stack>
  );
}
